import { useRef, useCallback, useEffect, RefObject } from 'react';

import endOfToday from 'date-fns/endOfToday';
import startOfToday from 'date-fns/startOfToday';

import { useLocale, noop, VALID_DATE_FORMATS } from '@adsk/alloy-react-helpers';
import Overlay, { useOverlay } from '@adsk/alloy-react-overlay';

import { DATE_PICKER_VARIANTS } from '../consts';
import { isPropControlled, getFieldValue } from '../helpers';
import { DatePickerArgs } from '../types';
import useSelection from './useSelection';
import useVisibility from './useVisibility';

export type UseDatePickerProps = Pick<
  DatePickerArgs,
  | 'date'
  | 'startDate'
  | 'endDate'
  | 'minDate'
  | 'maxDate'
  | 'onChange'
  | 'onHide'
  | 'defaultDate'
  | 'defaultEndDate'
  | 'defaultStartDate'
  | 'range'
  | 'disablePastDates'
  | 'disableFutureDates'
  | 'allowSameStartAndEndDate'
  | 'open'
  | 'defaultOpen'
  | 'variant'
  | 'error'
  | 'format'
  | 'disabled'
  | 'placeholder'
  | 'startDatePlaceholder'
  | 'endDatePlaceholder'
  | 'triggerProps'
> & { target?: RefObject<HTMLDivElement> };

function useDatePicker({
  /** core props */
  date: propsDate,
  startDate: propsStartDate,
  endDate: propsEndDate,
  minDate: propsMinDate,
  maxDate: propsMaxDate,
  onChange: propsOnChange = noop,
  defaultDate = null,
  defaultStartDate = null,
  defaultEndDate = null,
  range,
  disablePastDates,
  disableFutureDates,
  allowSameStartAndEndDate = false,
  /** picker props */
  open: propsOverlayOpen,
  defaultOpen,
  variant = DATE_PICKER_VARIANTS.FIELD,
  onHide,
  target,
  /** trigger props */
  error,
  format = VALID_DATE_FORMATS.SHORT,
  disabled,
  placeholder,
  startDatePlaceholder,
  endDatePlaceholder,
  triggerProps,
}: UseDatePickerProps) {
  const { T, locale } = useLocale(({ locale }) =>
    require(`../i18n/${locale}.json`)
  );

  const minDate = propsMinDate || (disablePastDates ? startOfToday() : null);
  const maxDate = propsMaxDate || (disableFutureDates ? endOfToday() : null);

  const overlayRef = useRef(null);

  const [internalOpen, internalToggleOpen] = useVisibility({
    disabled,
    propsOverlayOpen,
    defaultOpen,
  });

  const {
    show: open,
    placement,
    handleToggle: toggleOverlay,
    arrowProps,
    targetProps,
    overlayProps,
  } = useOverlay<HTMLDivElement>({
    show: internalOpen,
    target,
    overlayRef,
    onShow: () => {
      internalToggleOpen();
    },
    onHide: () => {
      internalToggleOpen();
      onHide?.();
    },
    offset: [0, variant === DATE_PICKER_VARIANTS.ICON ? 10 : 4],
    placement:
      variant === DATE_PICKER_VARIANTS.ICON
        ? Overlay.PLACEMENTS.BOTTOM
        : Overlay.PLACEMENTS.BOTTOM_START,
  });

  // executes when selecting a single date, or the end date in a range
  const onFinalSelection = useCallback(() => {
    if (!isPropControlled(propsOverlayOpen) && open) {
      toggleOverlay();
    }
  }, [propsOverlayOpen, toggleOverlay, open]);

  const { date, startDate, endDate, onDateSelected, resetSelection } =
    useSelection({
      range,
      defaultDate,
      defaultStartDate,
      defaultEndDate,
      propsDate,
      propsStartDate,
      propsEndDate,
      propsOnChange,
      allowSameStartAndEndDate,
      onFinalSelection,
    });

  useEffect(() => {
    if (!open) return () => false;
    const keydownHandler = ({ key }: KeyboardEvent) => {
      if (key === 'Backspace') resetSelection();
      if (key === 'Escape') toggleOverlay();
    };
    document.addEventListener('keydown', keydownHandler);
    return () => document.removeEventListener('keydown', keydownHandler);
  }, [open, resetSelection, toggleOverlay]);

  const getTriggerProps = useCallback(() => {
    return variant === DATE_PICKER_VARIANTS.FIELD
      ? {
          disabled,
          error,
          range,
          open,
          variant,
          placeholder:
            placeholder?.toString() || T.translate('SELECT_DATE')?.toString(),
          startDatePlaceholder:
            startDatePlaceholder ||
            placeholder ||
            T.translate('START_DATE')?.toString(),
          endDatePlaceholder:
            endDatePlaceholder ||
            placeholder ||
            T.translate('END_DATE')?.toString(),
          onClick: toggleOverlay,
          value: getFieldValue({
            date,
            startDate,
            endDate,
            range,
            format,
            locale,
          }),
          ...triggerProps,
          'data-testid': 'DatePickerTarget',
        }
      : {
          variant,
          disabled,
          onClick: toggleOverlay,
          ...triggerProps,
          'data-testid': 'DatePickerTarget',
        };
  }, [
    date,
    endDate,
    startDate,
    variant,
    disabled,
    error,
    range,
    open,
    placeholder,
    startDatePlaceholder,
    T,
    endDatePlaceholder,
    toggleOverlay,
    format,
    locale,
    triggerProps,
  ]);

  return {
    minDate,
    maxDate,
    open,
    date,
    variant,
    endDate,
    startDate,
    toggleOverlay,
    getTriggerProps,
    onDateSelected,
    resetSelection,
    // useOverlay
    placement,
    arrowProps,
    targetProps,
    overlayContainerProps: overlayProps,
  };
}

export default useDatePicker;
